import type { AxiosInstance, AxiosRequestConfig } from "axios";
import type {
  RequestConfig,
  ApiError,
  FileUploadConfig,
  PaginatedResponse,
} from "./types";
import { ErrorHandler } from "./errorHandler";

export abstract class BaseModule {
  protected httpClient: AxiosInstance;
  protected errorHandler: ErrorHandler;

  constructor(httpClient: AxiosInstance, errorHandler: ErrorHandler) {
    this.httpClient = httpClient;
    this.errorHandler = errorHandler;
  }

  private toApiError(error: unknown): ApiError {
    if (
      typeof error === "object" &&
      error !== null &&
      "message" in error &&
      "code" in error
    ) {
      const apiError = error as ApiError;
      return error instanceof Error
        ? apiError
        : this.errorHandler.handleError(apiError);
    }

    return this.errorHandler.handleError(error);
  }

  /**
   * Generic GET request
   */
  protected async get<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    try {
      const response = await this.httpClient.get<T>(endpoint, config);
      return response.data;
    } catch (error) {
      throw this.toApiError(error);
    }
  }

  /**
   * Generic POST request
   */
  protected async post<T>(
    endpoint: string,
    data?: unknown,
    config?: RequestConfig,
  ): Promise<T> {
    try {
      const response = await this.httpClient.post<T>(endpoint, data, config);
      return response.data;
    } catch (error) {
      throw this.toApiError(error);
    }
  }

  /**
   * Generic PUT request
   */
  protected async put<T>(
    endpoint: string,
    data?: unknown,
    config?: RequestConfig,
  ): Promise<T> {
    try {
      const response = await this.httpClient.put<T>(endpoint, data, config);
      return response.data;
    } catch (error) {
      throw this.toApiError(error);
    }
  }

  /**
   * Generic PATCH request
   */
  protected async patch<T>(
    endpoint: string,
    data?: unknown,
    config?: RequestConfig,
  ): Promise<T> {
    try {
      const response = await this.httpClient.patch<T>(endpoint, data, config);
      return response.data;
    } catch (error) {
      throw this.toApiError(error);
    }
  }

  /**
   * Generic DELETE request
   */
  protected async delete<T>(
    endpoint: string,
    config?: RequestConfig,
  ): Promise<T> {
    try {
      const response = await this.httpClient.delete<T>(endpoint, config);
      return response.data;
    } catch (error) {
      throw this.toApiError(error);
    }
  }

  /**
   * File upload with progress tracking
   */
  protected async uploadFile<T>(
    endpoint: string,
    file: File,
    config?: FileUploadConfig,
  ): Promise<T> {
    const formData = new FormData();
    formData.append("file", file);

    // Validate file if constraints are provided
    if (config?.maxFileSize && file.size > config.maxFileSize) {
      throw this.errorHandler.handleError(
        new Error(
          `File size exceeds maximum allowed size of ${config.maxFileSize} bytes`,
        ),
      );
    }

    if (config?.allowedTypes && !config.allowedTypes.includes(file.type)) {
      throw this.errorHandler.handleError(
        new Error(`File type ${file.type} is not allowed`),
      );
    }

    const requestConfig: AxiosRequestConfig = {
      ...config,
      headers: {
        ...config?.headers,
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        if (config?.onProgress && progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          config.onProgress(progress);
        }
      },
    };

    try {
      const response = await this.httpClient.post<T>(
        endpoint,
        formData,
        requestConfig,
      );
      return response.data;
    } catch (error) {
      throw this.toApiError(error);
    }
  }

  /**
   * Multiple file upload
   */
  protected async uploadFiles<T>(
    endpoint: string,
    files: File[],
    config?: FileUploadConfig,
  ): Promise<T> {
    const formData = new FormData();

    files.forEach((file, index) => {
      // Validate each file if constraints are provided
      if (config?.maxFileSize && file.size > config.maxFileSize) {
        throw this.errorHandler.handleError(
          new Error(`File ${file.name} exceeds maximum allowed size`),
        );
      }

      if (config?.allowedTypes && !config.allowedTypes.includes(file.type)) {
        throw this.errorHandler.handleError(
          new Error(`File type ${file.type} is not allowed for ${file.name}`),
        );
      }

      formData.append(`files[${index}]`, file);
    });

    const requestConfig: AxiosRequestConfig = {
      ...config,
      headers: {
        ...config?.headers,
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        if (config?.onProgress && progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          config.onProgress(progress);
        }
      },
    };

    try {
      const response = await this.httpClient.post<T>(
        endpoint,
        formData,
        requestConfig,
      );
      return response.data;
    } catch (error) {
      throw this.toApiError(error);
    }
  }

  /**
   * Download file as blob
   */
  protected async downloadFile(
    endpoint: string,
    config?: RequestConfig,
  ): Promise<Blob> {
    try {
      const response = await this.httpClient.get(endpoint, {
        ...config,
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      throw this.toApiError(error);
    }
  }

  /**
   * Paginated GET request
   */
  protected async getPaginated<T>(
    endpoint: string,
    page: number = 1,
    limit: number = 20,
    config?: RequestConfig,
  ): Promise<PaginatedResponse<T>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    // Add existing params from config
    if (config?.params) {
      Object.entries(config.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
    }

    const url = `${endpoint}?${params.toString()}`;

    try {
      const response = await this.httpClient.get<PaginatedResponse<T>>(url, {
        ...config,
        params: undefined, // Remove params since we're adding them to URL
      });
      return response.data;
    } catch (error) {
      throw this.toApiError(error);
    }
  }

  /**
   * Request with retry logic
   */
  protected async requestWithRetry<T>(
    requestFn: () => Promise<T>,
    maxRetries: number = 3,
  ): Promise<T> {
    let lastError: ApiError;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await requestFn();
      } catch (error) {
        lastError = this.toApiError(error);

        if (
          attempt === maxRetries ||
          !this.errorHandler.shouldRetry(lastError, attempt)
        ) {
          throw lastError;
        }

        // Wait before retrying
        const delay = this.errorHandler.getRetryDelay(attempt);
        await this.sleep(delay);
      }
    }

    throw lastError!;
  }

  /**
   * Build query string from parameters
   */
  protected buildQueryString(params: object): string {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach((item) => searchParams.append(key, String(item)));
        } else {
          searchParams.append(key, String(value));
        }
      }
    });

    return searchParams.toString();
  }

  // Axios serializes request Dates. API timestamps remain strings, as declared by DTOs.
  protected transformRequest<T>(data: T): T {
    return data;
  }
  protected transformResponse<T>(data: T): T {
    return data;
  }

  /**
   * Sleep utility for retry delays
   */
  protected sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
